import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';

gsap.registerPlugin(ScrollTrigger);
Alpine.plugin(collapse);

window.Alpine = Alpine;

Alpine.data('siteMenu', () => ({
  open: false,
  toggle() {
    this.open = !this.open;
  },
  close() {
    this.open = false;
  },
}));

Alpine.data('faqAccordion', () => ({
  active: null,

  toggle(index) {
    this.active = this.active === index ? null : index;
  },

  isOpen(index) {
    return this.active === index;
  },
}));

Alpine.data('publicationTabs', () => ({
  active: 0,

  setActive(index) {
    if (this.active === index) return;
    this.active = index;
  },
}));

Alpine.data('publishingRows', () => ({
  active: 0,
  next() {
    this.active += 1;
    this.$dispatch('publishing-next', { active: this.active });
  },
  previous() {
    this.active = Math.max(0, this.active - 1);
    this.$dispatch('publishing-previous', { active: this.active });
  },
}));

Alpine.start();

function initPublishingSliders() {
  document.querySelectorAll('[data-publishing-slider]').forEach((slider) => {
    if (slider._publishingSwiper) {
      slider._publishingSwiper.update();
      return;
    }

    const swiper = new Swiper(slider, {
      slidesPerView: 1,
      speed: 450,
      rewind: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      roundLengths: true,
      navigation: {
        prevEl: slider.querySelector('[data-publishing-slider-prev]'),
        nextEl: slider.querySelector('[data-publishing-slider-next]'),
      },
      pagination: {
        el: slider.querySelector('[data-publishing-slider-pagination]'),
        clickable: true,
      },
    });

    slider._publishingSwiper = swiper;

    let updateFrame = null;
    const updateSlider = () => {
      if (updateFrame) return;

      updateFrame = requestAnimationFrame(() => {
        updateFrame = null;
        swiper.update();
        ScrollTrigger.refresh();
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(updateSlider);
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(updateSlider);
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(updateSlider).observe(slider);
    }

    slider.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', updateSlider, { once: true });
    });
  });
}

function initSmoothScroll() {
  document.querySelectorAll('[data-scroll-link], a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const href = link.getAttribute('href');
      if (!href || href === '#' || !href.startsWith('#')) return;

      const target = document.querySelector(href);
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (history.pushState) {
        history.pushState(null, '', href);
      }
    });
  });
}

function initStickyHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  const update = () => {
    header.classList.toggle('is-sticky', window.scrollY > 24);
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
}

function initActiveNavLabel() {
  const header = document.querySelector('[data-site-header]');
  const labelEls = document.querySelectorAll('[data-menu-current-label]');
  const links = Array.from(document.querySelectorAll('[data-site-nav-link]'));

  if (!header || !labelEls.length || !links.length) return;

  const defaultLabel = header.dataset.defaultMenuLabel || 'Menu';
  const navItems = links
    .map((link) => {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return null;

      const target = document.getElementById(decodeURIComponent(href.slice(1)));
      if (!target) return null;

      return {
        label: link.dataset.navLabel || link.textContent.trim() || defaultLabel,
        target,
      };
    })
    .filter(Boolean);

  if (!navItems.length) return;

  const setLabel = (label) => {
    labelEls.forEach((el) => {
      el.textContent = label;
    });
  };

  const update = () => {
    const threshold = window.innerHeight * 0.35;
    const activeItem = navItems.reduce((active, item) => {
      const top = item.target.getBoundingClientRect().top;
      if (top > threshold) return active;
      if (!active || top > active.top) {
        return { ...item, top };
      }
      return active;
    }, null);

    setLabel(activeItem?.label || defaultLabel);
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
}

function initAcquisitionPanels() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  ScrollTrigger.matchMedia({
    '(min-width: 1024px)': () => {
      document.querySelectorAll('[data-acquisition-section]').forEach((section) => {
        const panelWrap = section.querySelector('[data-acquisition-panels]');
        const panels = gsap.utils.toArray(section.querySelectorAll('[data-acquisition-panel]'));

        if (!panelWrap || panels.length < 2) return;

        const tabHeight = 48;

        const setActivePanel = (activeIndex) => {
          panels.forEach((panel, index) => {
            panel.classList.toggle('is-active', index === activeIndex);
          });
        };

        const getStackedY = (index) => index * tabHeight;

        const getCollapsedY = (index) => {
          const remainingPanels = panels.length - index;
          return panelWrap.offsetHeight - tabHeight * remainingPanels;
        };

        let refreshFrame = null;
        const scheduleRefresh = () => {
          if (refreshFrame) return;

          refreshFrame = requestAnimationFrame(() => {
            refreshFrame = null;
            ScrollTrigger.refresh();
          });
        };

        const setPanelPositions = () => {
          panels.forEach((panel, index) => {
            gsap.set(panel, {
              y: index === 0 ? 0 : getCollapsedY(index),
              zIndex: index + 1,
            });
          });

          setActivePanel(0);

          
        };

        setPanelPositions();

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: `+=${panels.length * 90}%`,
            pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          refreshPriority: 1,
          onRefresh: (self) => {
            setPanelPositions();

            if (self.pin && self.pin.parentElement) {
              self.pin.parentElement.style.backgroundColor = '#fff';
              }
            },
            onUpdate: (self) => {
              const activeIndex = Math.min(
                panels.length - 1,
                Math.round(self.progress * (panels.length - 1))
              );

              setActivePanel(activeIndex);
            },
          },
        });

        panels.forEach((panel, index) => {
          if (index === 0) return;

          timeline.to(
            panel,
            {
              y: getStackedY(index),
              ease: 'none',
              duration: 1,
            },
            index - 1
          );
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(scheduleRefresh);
        });

        if (document.fonts?.ready) {
          document.fonts.ready.then(scheduleRefresh);
        }

        if ('ResizeObserver' in window) {
          new ResizeObserver(scheduleRefresh).observe(panelWrap);
        }

        section.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;
          image.addEventListener('load', scheduleRefresh, { once: true });
        });
      });
    },
  });
}

function initReveals() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const revealSelector = [
    '.section-heading',
    '.section-offset p',
    '.section-offset .prose',
    '.section-offset article',
    '.section-offset img',
    '.section-offset nav',
    '.section-offset a[href]',
    '.section-offset button',
  ].join(',');

  const revealItems = gsap.utils.toArray(revealSelector).filter((item) => {
    if (item.closest('[data-site-header]')) return false;
    if (item.closest('[data-acquisition-panel]')) return false;
    if (item.closest('[data-publishing-slider]')) return false;
    if (item.closest('[data-publication-image-panel]')) return false;

    const revealParent = item.parentElement?.closest(revealSelector);
    return !revealParent || revealParent.classList.contains('section-heading');
  });

  revealItems.forEach((item) => {
    gsap.from(item, {
      y: 28,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: item,
        start: 'top 85%',
        once: true,
      },
    });
  });
}

window.addEventListener('DOMContentLoaded', () => {
  initSmoothScroll();
  initStickyHeader();
  initActiveNavLabel();
  initPublishingSliders();
  initAcquisitionPanels();
  initReveals();
});

window.addEventListener('load', () => {
  initPublishingSliders();
  ScrollTrigger.refresh();
});
